import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { getErrorMessage } from '@/shared/utils/error';
import type { RootState } from '@/store';

import type {
  AlvoDoPacote,
  ArquivoAssinado,
  ArquivoDoPacote,
  AutosDoProcesso,
  ChaveDaSecao,
  OpcoesDosAutos,
  PacoteDosAutos,
  PacoteRecebido,
  RecusaDosAutos,
  RegistroDeAnexos,
} from '../types/baixarProcesso.types';
import { esperandoPacote } from '../utils/esperaDoPacote';
import {
  autosPdfService,
  RecusaDosAutosError,
  recusaDoPacoteQueFalhou,
  SemRespostaDoServidorError,
} from './autosPdfService';

/**
 * Recusa que não tem o que tentar de novo, venha de qual chamada vier: o painel troca para a
 * etapa do motivo (`semPermissao`/`indisponivel`) em vez de oferecer outra tentativa.
 *
 * @example recusaDefinitiva({ motivo: 'semPermissao', mensagem: '…' }) // true
 */
export function recusaDefinitiva(
  recusa: RecusaDosAutos | undefined,
): recusa is RecusaDosAutos & { motivo: 'semPermissao' | 'indisponivel' } {
  return recusa?.motivo === 'semPermissao' || recusa?.motivo === 'indisponivel';
}

function recusaDe(error: unknown): RecusaDosAutos {
  if (error instanceof RecusaDosAutosError) {
    return { motivo: error.motivo, mensagem: error.message, erroCodigo: error.erroCodigo };
  }
  return { motivo: 'erro', mensagem: getErrorMessage(error) };
}

/**
 * Um pacote que já nasce `FALHOU` vira rejeição aqui, e não estado de "pronto com erro": a tela
 * só conhece falha pelo caminho do erro.
 */
function recebido(pacote: PacoteDosAutos): PacoteRecebido {
  if (pacote.status === 'FALHOU') throw recusaDoPacoteQueFalhou(pacote.erroCodigo);
  return { pacote, respondidoEm: new Date().toISOString() };
}

export const listarAutos = createAsyncThunk<
  OpcoesDosAutos,
  AutosDoProcesso,
  { rejectValue: RecusaDosAutos; state: RootState }
>('baixarProcesso/listarAutos', async (autos, { rejectWithValue, signal }) => {
  try {
    return await autosPdfService.listarAutos(autos, signal);
  } catch (error) {
    return rejectWithValue(recusaDe(error));
  }
});

/**
 * Sem `secoes`, o documento completo (que é também o ZIP). `condition` barra a reentrância —
 * clique duplo em "Baixar" ou "Tentar novamente" produzia dois pedidos em voo, e o que
 * respondesse por último vencia.
 */
export const pedirPacote = createAsyncThunk<
  PacoteRecebido,
  AutosDoProcesso & { secoes?: readonly ChaveDaSecao[] },
  { rejectValue: RecusaDosAutos; state: RootState }
>(
  'baixarProcesso/pedirPacote',
  async (pedido, { rejectWithValue, signal }) => {
    try {
      return recebido(await autosPdfService.pedirPacote(pedido, pedido.secoes, signal));
    } catch (error) {
      return rejectWithValue(recusaDe(error));
    }
  },
  {
    condition: (_pedido, { getState }) => !esperandoPacote(getState().baixarProcesso.etapa),
  },
);

/**
 * Abre o pacote que o e-mail de "pronto" anunciou — ESTE pacote, e não o da versão atual: é o
 * arquivo que a pessoa foi avisada que existe, com o código de validação dele.
 */
export const abrirPacoteDoAviso = createAsyncThunk<
  PacoteRecebido,
  AlvoDoPacote,
  { rejectValue: RecusaDosAutos; state: RootState }
>('baixarProcesso/abrirPacoteDoAviso', async (alvo, { rejectWithValue, signal }) => {
  try {
    return recebido(await autosPdfService.consultarPacote(alvo, signal));
  } catch (error) {
    return rejectWithValue(recusaDe(error));
  }
});

/** Cada leitura do acompanhamento: é o que leva o painel de "na fila" para "processando". */
export const pacoteConsultado = createAction<PacoteDosAutos>('baixarProcesso/pacoteConsultado');

/**
 * `semResposta`: o servidor parou de responder às consultas, mas o pacote segue sendo gerado lá —
 * não é falha, e quem espera vai para o e-mail. `recusado`: o próprio pacote terminou em erro, ou
 * o servidor recusou a consulta (o 403 de quem perdeu o acesso no meio da espera).
 */
type RejeicaoDoAcompanhamento =
  | { motivo: 'semResposta' }
  | { motivo: 'recusado'; recusa: RecusaDosAutos };

/**
 * Acompanha o pacote em geração até ele terminar. Sem prazo: quem desiste é o painel, abortando
 * o pedido (mandou para o e-mail, cancelou, fechou).
 */
export const acompanharPacote = createAsyncThunk<
  PacoteDosAutos,
  AlvoDoPacote,
  { rejectValue: RejeicaoDoAcompanhamento; state: RootState }
>('baixarProcesso/acompanharPacote', async (alvo, { dispatch, rejectWithValue, signal }) => {
  try {
    return await autosPdfService.acompanharAteTerminar(alvo, {
      signal,
      aoConsultar: (pacote) => dispatch(pacoteConsultado(pacote)),
    });
  } catch (error) {
    if (error instanceof SemRespostaDoServidorError) {
      return rejectWithValue({ motivo: 'semResposta' });
    }
    return rejectWithValue({ motivo: 'recusado', recusa: recusaDe(error) });
  }
});

/**
 * Pede o e-mail de quando o pacote terminar. `aoSair`: a pessoa fechou o painel ou a página — o
 * pedido segue (keepalive) mas não mexe no painel, que já foi fechado.
 */
export const avisarPacotePorEmail = createAsyncThunk<
  void,
  AlvoDoPacote & { aoSair: boolean },
  { rejectValue: RecusaDosAutos; state: RootState }
>('baixarProcesso/avisarPacotePorEmail', async (pedido, { rejectWithValue }) => {
  try {
    await autosPdfService.pedirAvisoPorEmail(pedido, { aoSair: pedido.aoSair });
  } catch (error) {
    return rejectWithValue(recusaDe(error));
  }
});

/** A URL assinada vence em minutos: o painel pede uma a cada clique em baixar. */
export const assinarArquivo = createAsyncThunk<
  ArquivoAssinado,
  AlvoDoPacote & { arquivo: ArquivoDoPacote },
  { rejectValue: RecusaDosAutos; state: RootState }
>('baixarProcesso/assinarArquivo', async (pedido, { rejectWithValue }) => {
  try {
    return await autosPdfService.assinarArquivo(pedido, pedido.arquivo);
  } catch (error) {
    return rejectWithValue(recusaDe(error));
  }
});

/**
 * O registro de anexos do pacote pronto: os que não entraram no PDF (e, na Secretaria, todos) e
 * se a pessoa ainda deve a ciência que libera o download.
 */
export const carregarRegistroDeAnexos = createAsyncThunk<
  RegistroDeAnexos,
  AlvoDoPacote,
  { rejectValue: RecusaDosAutos; state: RootState }
>('baixarProcesso/carregarRegistroDeAnexos', async (alvo, { rejectWithValue, signal }) => {
  try {
    return await autosPdfService.registroDeAnexos(alvo, signal);
  } catch (error) {
    return rejectWithValue(recusaDe(error));
  }
});

/** "Estou ciente" dos anexos fora do PDF: o servidor registra e passa a liberar o download. */
export const registrarCienciaDosAnexos = createAsyncThunk<
  { cienciaEm: string },
  AlvoDoPacote,
  { rejectValue: RecusaDosAutos; state: RootState }
>('baixarProcesso/registrarCienciaDosAnexos', async (alvo, { rejectWithValue }) => {
  try {
    return await autosPdfService.registrarCiencia(alvo);
  } catch (error) {
    return rejectWithValue(recusaDe(error));
  }
});
